import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, CircularProgress, LinearProgress, Paper, Typography,
} from '@material-ui/core';
import { useCatalogSync } from '../../home/hooks/useCatalogSync';
import { serviceRegistry } from '../../../store';
import { IndexingStatus } from '../../home/operatorState';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import CatalogSelectorSection from '../../../pluginAdmin/features/catalogSelector/CatalogSelectorSection';
import IndexingHistorySection from '../../data-settings/IndexingHistorySection';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/**
 * DataSettingsEmbedded — bridge-only Data & Connectivity panel rendered inside
 * the embedded production shell.  Zero legacy web-service calls.


 * • Indexing status + actions come from useCatalogSync (bridge polling).
 * • Recent job history is fetched via indexingApi.getHistory (bridge transport).
 * • indexingApi defaults to serviceRegistry.indexingApi (bridge on production
 *   path, null on legacy path — this component is never rendered on legacy path).

 * direct BridgeClient in UI code.
 */
export default function DataSettingsEmbedded({ indexingApi = serviceRegistry.indexingApi }) {
  const t = useLocalizedMessage();
  const { capabilities } = useShellAppContext();
  const {
    status,
    progress,
    indexedCount,
    totalCount,
    error,
    isLoading,
    isStarting,
    isDegraded,
    startFullUpdate,
  } = useCatalogSync();

  const isRunning = status === IndexingStatus.RUNNING || isStarting;
  const canStart = !isLoading && !isRunning;
  // this hook's `status` enum already accounts for can_search (READY ⇔

  const statusLabel = t('search.data.indexing.statusValue', {
    status: isDegraded ? 'degraded' : String(status || 'idle'),
  });

  return (
    <Box data-testid="data-settings-embedded-panel">

      <Paper elevation={0} style={{ padding: 16, marginBottom: 16 }}>
        <Typography variant="h6" gutterBottom><IntlMessages id="ui.searchSettings.dataSettingsEmbedded.catalogIndexing" /></Typography>

        {isLoading ? (
          <Box display="flex" alignItems="center">
            <CircularProgress size={16} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginLeft: 8 }}
            >
              <IntlMessages id="common.loading" />
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" data-testid="data-indexing-status">
            <IntlMessages id="ui.searchSettings.dataSettingsEmbedded.status" />
            {statusLabel}
            {totalCount > 0 && (
              t('search.data.indexing.indexedCount', {
                indexed: indexedCount.toLocaleString(),
                total: totalCount.toLocaleString(),
              })
            )}
          </Typography>
        )}

        {isRunning && !isDegraded && (
          <Box mt={1} mb={1}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, progress || 0))}
            />
          </Box>
        )}

        {error && (
          <Typography
            variant="body2"
            color="error"
            style={{ marginTop: 8 }}
            data-testid="data-indexing-error"
          >
            {error}
          </Typography>
        )}

        <Box mt={2} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={startFullUpdate}
            disabled={!canStart}
            data-testid="data-start-indexing-btn"
          >
            {t(isStarting ? 'search.data.indexing.starting' : 'search.data.indexing.start')}
          </Button>
        </Box>
      </Paper>

      <IndexingHistorySection indexingApi={indexingApi} />

      <CatalogSelectorSection capabilities={capabilities || {}} />
    </Box>
  );
}

DataSettingsEmbedded.propTypes = {
  indexingApi: PropTypes.shape({
    getHistory: PropTypes.func.isRequired,
  }),
};

DataSettingsEmbedded.defaultProps = {
  indexingApi: undefined,
};
